import { action, makeObservable, observable, reaction } from 'mobx';

import type {
  LocalTerminalExecResult,
  LocalTerminalSessionOpened,
} from '@/features/electron-api/features/LocalTerminalAPI';

const MAX_RUNS = 200;

const STORAGE_KEY = 'terminal-agent-runs';
/** Finished runs kept across reloads. Output is capped per run to bound the blob. */
const PERSISTED_MAX_RUNS = 100;
/** Output often contains secrets; a day is plenty for "what did it run earlier". */
const PERSISTED_TTL_MS = 24 * 60 * 60_000;
const PERSISTED_MAX_OUTPUT_CHARS = 16_000;
const PERSIST_DEBOUNCE_MS = 250;

/** One agent-run command (`TERMINAL_RUN`) as a host session, start to finish. */
export interface AgentRun {
  sessionId: string;
  command: string;
  cwd: string;
  /** Backend tool-call id, echoed end to end; exact card-to-run binding. */
  toolCallId?: string;
  startedAt: number;
  finishedAt: number | null;
  /** The exec result once the command ended; null while it runs. */
  result: LocalTerminalExecResult | null;
  /**
   * Reasoning step (chat card) this run belongs to, once a card has claimed it.
   * Set the first time a card renders while the run is the latest unclaimed one
   * for its command line; from then on the pairing is exact and survives reloads.
   */
  stepId?: string;
}

/**
 * Every field a card reads, checked - a stale or hand-edited localStorage entry
 * must not be able to throw inside a render.
 */
function isPersistedRun(r: unknown): r is AgentRun {
  if (!r || typeof r !== 'object') return false;
  const run = r as Record<string, unknown>;
  if (
    typeof run.sessionId !== 'string' ||
    typeof run.command !== 'string' ||
    typeof run.cwd !== 'string' ||
    typeof run.startedAt !== 'number' ||
    typeof run.finishedAt !== 'number' ||
    (run.toolCallId !== undefined && typeof run.toolCallId !== 'string') ||
    (run.stepId !== undefined && typeof run.stepId !== 'string')
  ) {
    return false;
  }
  const res = run.result;
  if (!res || typeof res !== 'object') return false;
  const x = res as Record<string, unknown>;
  return (
    (x.exitCode === null || typeof x.exitCode === 'number') &&
    (x.signal === null || typeof x.signal === 'string') &&
    typeof x.timedOut === 'boolean' &&
    typeof x.durationMs === 'number' &&
    typeof x.output === 'string' &&
    typeof x.truncated === 'boolean'
  );
}

function loadPersistedRuns(): AgentRun[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - PERSISTED_TTL_MS;
    // isPersistedRun guarantees finishedAt is a number
    const live = parsed.filter(
      (r): r is AgentRun => isPersistedRun(r) && (r.finishedAt ?? 0) > cutoff
    );
    // Expired output leaves the disk now, not at the next write.
    if (live.length !== parsed.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(live));
    }
    return live;
  } catch {
    return [];
  }
}

function persistRuns(runs: readonly AgentRun[]): void {
  if (typeof window === 'undefined') return;
  const finished = runs
    .filter((r) => r.result !== null)
    .slice(-PERSISTED_MAX_RUNS)
    .map((r) => {
      const output = r.result!.output;
      return output.length <= PERSISTED_MAX_OUTPUT_CHARS
        ? r
        : {
            ...r,
            result: {
              ...r.result!,
              output: output.slice(-PERSISTED_MAX_OUTPUT_CHARS),
              truncated: true,
            },
          };
    });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(finished));
  } catch {
    // quota or private mode - the cards just fall back to "no output" after reload
  }
}

/**
 * Agent-run commands as the renderer knows them. Main reports when the host
 * spawns one (`session_opened`) and when it finishes (`session-closed`, with the
 * exec result). Runs are headless - no terminal tab unless the user opens one
 * from the chat card - so this store is what the card renders from.
 *
 * Finished runs are persisted to localStorage: the backend does not store
 * `tool_execute` tool-call records, so without this a reloaded chat would show
 * the command but no exit code or output. Running ones are not persisted; after
 * a reload the host is the only source of truth for those.
 */
export class AgentRunsStore {
  runs: AgentRun[];
  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Runs that started before this are rehydrated from localStorage. They stay
   * reachable by their exact keys (tool call id, step, session) so a reloaded
   * card keeps its status, but a guess by command line must not reach them: a
   * fresh card with the same line as one run yesterday would show that output.
   */
  private readonly bootedAt = Date.now();

  constructor(initialRuns: AgentRun[] = loadPersistedRuns()) {
    this.runs = initialRuns;
    makeObservable(this, {
      runs: observable.shallow,
      start: action,
      finish: action,
      bindStep: action,
    });
    reaction(
      () => this.runs,
      (runs) => this.schedulePersist(runs)
    );
  }

  /** Debounced: a finishing run mutates twice in quick succession (finish, bind). */
  private schedulePersist(runs: readonly AgentRun[]): void {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      persistRuns(runs);
    }, PERSIST_DEBOUNCE_MS);
  }

  start(event: LocalTerminalSessionOpened): void {
    if (this.runs.some((r) => r.sessionId === event.sessionId)) return;
    const run: AgentRun = {
      sessionId: event.sessionId,
      command: event.command.trim(),
      cwd: event.cwd,
      toolCallId: event.toolCallId,
      startedAt: Date.now(),
      finishedAt: null,
      result: null,
    };
    this.runs = [...this.runs.slice(-(MAX_RUNS - 1)), run];
  }

  finish(result: LocalTerminalExecResult): void {
    const now = Date.now();
    const index = this.runs.findIndex((r) => r.sessionId === result.sessionId);
    if (index === -1) {
      // finished before we saw it start (fast command, or the opened event was lost)
      this.runs = [
        ...this.runs.slice(-(MAX_RUNS - 1)),
        {
          sessionId: result.sessionId,
          command: result.command.trim(),
          cwd: result.cwd,
          toolCallId: result.toolCallId,
          startedAt: now - result.durationMs,
          finishedAt: now,
          result,
        },
      ];
      return;
    }
    const finished: AgentRun = { ...this.runs[index], finishedAt: now, result };
    this.runs = this.runs.map((r, i) => (i === index ? finished : r));
  }

  bySession(sessionId: string): AgentRun | undefined {
    return this.runs.find((r) => r.sessionId === sessionId);
  }

  byStep(stepId: string): AgentRun | undefined {
    return this.runs.find((r) => r.stepId === stepId);
  }

  /**
   * Run of `command` bound to any step of the same message as `stepId`. Step ids
   * embed the modality index, which can shift between the streaming and the
   * finalized message; the message id and command line do not.
   */
  bySiblingStep(stepId: string, command: string): AgentRun | undefined {
    const messageId = messageIdOfStep(stepId);
    if (!messageId) return undefined;
    const prefix = `modality-${messageId}-`;
    const wanted = command.trim();
    return this.runs.find(
      (r) => r.command === wanted && r.stepId?.startsWith(prefix) === true
    );
  }

  /** Run started by this backend tool call (the host echoes the id end to end). */
  byToolCallId(toolCallId: string): AgentRun | undefined {
    return this.runs.find((r) => r.toolCallId === toolCallId);
  }

  /** Claim `run` for the card at `stepId` (no-op if already claimed by anyone). */
  bindStep(sessionId: string, stepId: string): void {
    const index = this.runs.findIndex((r) => r.sessionId === sessionId);
    if (index === -1 || this.runs[index].stepId !== undefined) return;
    const bound: AgentRun = { ...this.runs[index], stepId };
    this.runs = this.runs.map((r, i) => (i === index ? bound : r));
  }

  /**
   * Most recent run of this exact command line not yet claimed by a card, from
   * this page's lifetime only. A card uses this to find its run before a step
   * binding exists; the agent rarely has the same line in flight twice, so
   * latest-unclaimed is the right guess. Rehydrated runs are excluded: their
   * cards find them by tool call id, step or session, never by command line.
   */
  latestUnboundByCommand(command: string): AgentRun | undefined {
    const wanted = command.trim();
    for (let i = this.runs.length - 1; i >= 0; i--) {
      const run = this.runs[i];
      if (run.startedAt < this.bootedAt) continue;
      if (run.command === wanted && run.stepId === undefined) return run;
    }
    return undefined;
  }
}

// One instance per page, across Vite HMR re-evaluations of this module. Any
// edit that reaches this file (or a dependency) re-runs it; a fresh store here
// would be the one cards observe while LocalTerminalPanel's subscription keeps
// feeding the instance it captured at mount - cards then never see another run.
// Copying runs across via import.meta.hot.data is not enough: it covers runs up
// to the edit, not after. Share the instance itself.
const STORE_GLOBAL = Symbol.for('littlebird.agentRunsStore');
type StoreGlobal = typeof globalThis & { [STORE_GLOBAL]?: AgentRunsStore };
const g = globalThis as StoreGlobal;
export const agentRunsStore: AgentRunsStore = (g[STORE_GLOBAL] ??=
  new AgentRunsStore());

/** `modality-<messageId>-<type>-<index>` -> `<messageId>` (see ui-utils message-grouping). */
export function messageIdOfStep(stepId: string): string | null {
  const match = /^modality-(.+)-[a-z_]+-\d+$/.exec(stepId);
  return match ? match[1] : null;
}
